import {
  ChangeDetectionStrategy,
  Component,
  Input,
  numberAttribute,
  OnInit
} from '@angular/core';
import { inRange } from 'lodash-es';
import { RangeColorMap } from './m-circular-progress.component.types';
import { colors } from '../../styles/colors/colors.config';

import HighchartsMore from 'highcharts/highcharts-more';
import * as Highcharts from 'highcharts';
import HC_solidGauge from 'highcharts/modules/solid-gauge';
import { HighchartsChartModule } from 'highcharts-angular';

HighchartsMore(Highcharts);
HC_solidGauge(Highcharts);

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'm-circular-progress',
  templateUrl: './m-circular-progress.component.html',
  imports: [HighchartsChartModule]
})
export class MCircularProgressComponent implements OnInit {
  @Input({ transform: numberAttribute }) value!: number | null; // Progress value (0 - 100)
  @Input() size = 48; // Size of the component in px
  @Input() textStyleClass = ''; // Additional style class for percentage text
  @Input() noValuePlaceholder = '-';
  @Input() animate = true;
  @Input() rangeColorMap: RangeColorMap[] = [
    { range: [80, 100], color: String(colors['green-700']) },
    { range: [40, 79], color: String(colors['yellow-700']) },
    { range: [0, 39], color: String(colors['red-700']) }
  ];

  private readonly defaultColor = colors['gray-700'];

  // eslint-disable-next-line @typescript-eslint/naming-convention
  Highcharts: typeof Highcharts = Highcharts; // required
  chartOptions: Highcharts.Options = {};

  private isValidColorRangeMapping(): boolean {
    this.rangeColorMap.sort(
      (a, b) => (a.range[0] as number) - (b.range[0] as number)
    );

    for (let i = 0; i < this.rangeColorMap.length; i++) {
      const currentRange = (this.rangeColorMap[i] as RangeColorMap).range;
      const nextRange = this.rangeColorMap[i + 1]?.range;

      if (
        (nextRange && currentRange[1] !== (nextRange[0] as number) - 1) ||
        (currentRange[1] as number) > 100
      ) {
        return false;
      }
    }

    return true;
  }

  get valueText(): string {
    const defaultClasses = 'b2 text-gray-1000 mt-0.5';
    const styleClass = this.textStyleClass || defaultClasses;
    return `<div class="${styleClass}">${
      this.value !== null && !isNaN(this.value)
        ? this.value + '%'
        : this.noValuePlaceholder
    }</div>`;
  }

  private getColor(value: number): string {
    let color = this.defaultColor;
    this.rangeColorMap.forEach((rangeMap) => {
      if (
        inRange(
          value,
          rangeMap.range[0] as number,
          (rangeMap.range[1] as number) + 1
        )
      ) {
        color = rangeMap.color;
      }
    });
    return String(color);
  }

  ngOnInit(): void {
    if (this.isValidColorRangeMapping()) {
      this.chartOptions = {
        chart: {
          type: 'solidgauge',
          backgroundColor: 'transparent',
          height: this.size,
          width: this.size,
          margin: 0
        },
        navigation: {
          buttonOptions: {
            enabled: false
          }
        },
        title: {
          align: 'center',
          text: this.valueText,
          useHTML: true,
          verticalAlign: 'middle'
        },
        pane: {
          center: ['50%', '50%'],
          size: this.size,
          startAngle: 0,
          endAngle: 360,
          background: [
            {
              outerRadius: '100%',
              innerRadius: '83%',
              backgroundColor: colors['gray-300'],
              borderWidth: 0
            }
          ]
        },
        yAxis: {
          min: 0,
          max: 100,
          lineWidth: 0,
          tickPositions: []
        },
        tooltip: {
          enabled: false
        },
        plotOptions: {
          solidgauge: {
            innerRadius: '83%',
            dataLabels: {
              enabled: false
            },
            linecap: 'round',
            rounded: true
          }
        },
        series: [
          {
            type: 'solidgauge',
            name: 'Circular Percent',
            data: [
              {
                y: this.value || null,
                color: this.value ? this.getColor(this.value) : undefined
              }
            ],
            dataLabels: {
              enabled: false
            },
            animation: this.animate
          }
        ],
        credits: {
          enabled: false
        }
      };
    } else {
      throw new Error('Invalid color range mapping provided!');
    }
  }
}
